<template>
  <section class="hrh-page" :dir="pageDirection">
    <div v-if="loading" class="page-loading">
      <div class="loader-card">
        <div class="loader-ring"></div>

        <h3>{{ tr("loadingTitle") }}</h3>

        <p>{{ tr("loadingText") }}</p>
      </div>
    </div>

    <div class="page-header">
      <h1>{{ tr("pageTitle") }}</h1>

      <p>{{ tr("pageSubtitle") }}</p>
    </div>

    <div class="top-row">
      <div class="counter">{{ tr("indicatorsTotal", { count: 9 }) }}</div>

      <select>
        <option>{{ tr("pageTitle") }}</option>
      </select>
    </div>

    <div class="axis-summary">
      <div class="axis-card-header">
        <div>
          <h2>{{ tr("axisTitle") }}</h2>

          <p>{{ tr("axisDescription") }}</p>

          <strong>{{ tr("axisNote") }}</strong>
        </div>

        <div class="axis-score">
          <span> {{ axisAverage !== null ? `${axisAverage}%` : "-" }}</span>

          <small>
            {{ tr("fisWeight") }}:

            {{ axisWeight !== null ? `${axisWeight}%` : "-" }}</small
          >
        </div>
      </div>

      <div class="axis-stats">
        <div>
          <b>{{ hrhAchievedCount }}</b>

          <span>{{ tr("achieved") }}</span>
        </div>

        <div>
          <b>{{ hrhBelowStandardCount }}</b>

          <span>{{ tr("belowStandard") }}</span>
        </div>

        <div>
          <b>{{ hrhCriticalCount }}</b>

          <span>{{ tr("critical") }}</span>
        </div>
      </div>
    </div>

    <div class="axis-indicator-grid">
      <article
        v-for="kpi in hrhKpis"
        :key="kpi.code"
        class="axis-indicator-card"
        :class="{ flag: kpiStatus(kpi) === 'r' }"
        :data-critical-label="tr('criticalFlag')"
      >
        <div class="axis-indicator-top">
          <span class="code">{{ kpi.code }}</span>

          <span v-if="kpi.composite" class="composite">{{
            tr("composite")
          }}</span>
        </div>

        <h3>{{ kpi.name }}</h3>

        <div class="axis-indicator-value">
          <strong :style="{ color: statusColor(kpi) }">{{ kpi.val }}</strong>

          <span>{{ kpi.unit }}</span>

          <em
            >{{ tr("standard") }} {{ kpi.dir === "lt" ? "≤" : "≥" }}

            {{ kpi.std }}</em
          >
        </div>

        <div class="gauge">
          <div
            :style="{
              width: barWidth(kpi) + '%',

              background: statusColor(kpi),
            }"
          ></div>
        </div>

        <div class="axis-indicator-foot">
          <span>
            {{ tr("achievement") }}:

            <b :style="{ color: statusColor(kpi) }">{{ achieve(kpi) }}%</b>
          </span>

          <span :class="['rag', kpiStatus(kpi)]">
            {{ statusLabel[kpiStatus(kpi)] }}
          </span>
        </div>

        <div v-if="kpi.chart" class="axis-mini-chart">
          <h4>{{ kpi.chart.label }}</h4>

          <apexchart
            v-if="chartsReady"
            :type="kpiApex(kpi).type"
            height="180"
            :series="kpiApex(kpi).series"
            :options="kpiApex(kpi).options"
          />
        </div>
      </article>
    </div>

    <div class="axis-card hidden">
      <div class="axis-title">
        <div>
          <span class="dot"></span>

          <strong>{{ tr("dashboardTitle") }}</strong>

          <small>{{ tr("dashboardMeta") }}</small>
        </div>
      </div>

      <p class="desc">{{ tr("dashboardDescription") }}</p>

      <div class="info-box">
        <p>
          <strong>{{ tr("totalApproved") }}:</strong>

          {{ totalApproved }} ·

          <strong>{{ tr("totalOccupied") }}:</strong>

          {{ totalOccupied }} ·

          <strong>{{ tr("dailyAvailability") }}:</strong>

          \~85% ·

          <strong>{{ tr("coverage247") }}:</strong>

          {{ tr("partial") }} ·

          <strong>{{ tr("trainingLast12Months") }}:</strong>

          {{ tr("variable") }}
        </p>
      </div>

      <div class="kpi-grid">
        <div class="kpi-card red">
          <div class="kpi-value">{{ avgVacancies }}</div>

          <div class="kpi-label">{{ tr("averageVacancies") }}</div>
        </div>

        <div class="kpi-card orange">
          <div class="kpi-value">{{ avgAbsentStaff }}</div>

          <div class="kpi-label">{{ tr("averageAbsent") }}</div>
        </div>

        <div class="kpi-card purple">
          <div class="kpi-value">{{ avgFemalePercent }}%</div>

          <div class="kpi-label">{{ tr("averageFemale") }}</div>
        </div>

        <div class="kpi-card green">
          <div class="kpi-value">{{ emergencyPlanCount }}</div>

          <div class="kpi-label">{{ tr("emergencyPlanFacilities") }}</div>
        </div>
      </div>

      <div class="charts-grid">
        <div class="chart-card">
          <div class="section-title">
            {{ tr("departmentsWithoutQualified") }}
          </div>

          <apexchart
            v-if="chartsReady"
            type="bar"
            height="320"
            :series="[{ name: tr('facilityCount'), data: departmentSeries }]"
            :options="barOptions(departmentLabels)"
          />
        </div>

        <div class="chart-card">
          <div class="section-title">{{ tr("nightShiftAdequacy") }}</div>

          <apexchart
            v-if="chartsReady"
            type="donut"
            height="320"
            :series="nightShiftSeries"
            :options="donutOptions(nightShiftLabels)"
          />
        </div>

        <div class="chart-card">
          <div class="section-title">{{ tr("trainingDuring12Months") }}</div>

          <apexchart
            v-if="chartsReady"
            type="donut"
            height="320"
            :series="trainingSeries"
            :options="donutOptions(trainingLabels)"
          />
        </div>

        <div class="chart-card">
          <div class="section-title">{{ tr("approvedVsOccupied") }}</div>

          <apexchart
            v-if="chartsReady"
            type="bar"
            height="320"
            :series="positionsSeries"
            :options="barOptions(facilityLabels)"
          />
        </div>
      </div>

      <div class="table-wrap mb-table">
        <div class="table-title">
          {{ tr("staffDetails") }}

          <span>{{
            tr("facilityCountValue", { count: staffings.length })
          }}</span>
        </div>

        <table class="staff-table">
          <thead>
            <tr>
              <th>{{ tr("table.facility") }}</th>

              <th>{{ tr("table.approvedPositions") }}</th>

              <th>{{ tr("table.occupiedPositions") }}</th>

              <th>{{ tr("table.vacancies") }}</th>

              <th>{{ tr("table.absent") }}</th>

              <th>{{ tr("table.femalePercent") }}</th>

              <th>{{ tr("table.emergencyPlan") }}</th>

              <th>{{ tr("table.unqualifiedDepartments") }}</th>

              <th>{{ tr("table.nightShift") }}</th>

              <th>{{ tr("table.recentTraining") }}</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="item in staffings" :key="item.id">
              <td class="name">{{ item.facility_name }}</td>

              <td>{{ item.approved_positions }}</td>

              <td>{{ item.occupied_positions }}</td>

              <td>{{ item.vacancies }}</td>

              <td>{{ item.absent_staff }}</td>

              <td>{{ item.female_percent }}%</td>

              <td>
                <span
                  :class="[
                    'status-badge',

                    getStatusColor(item.has_written_emergency_plan),
                  ]"
                >
                  {{ displayStatus(item.has_written_emergency_plan) }}
                </span>
              </td>

              <td>
                {{
                  displayDepartments(item.departments_without_qualified_staff)
                }}
              </td>

              <td>
                <span
                  :class="[
                    'status-badge',

                    getShiftColor(item.night_shift_adequacy),
                  ]"
                >
                  {{ displayStatus(item.night_shift_adequacy) }}
                </span>
              </td>

              <td>
                <span
                  :class="[
                    'status-badge',

                    getStatusColor(item.recent_training_12_months),
                  ]"
                >
                  {{ displayStatus(item.recent_training_12_months) }}
                </span>
              </td>
            </tr>

            <tr v-if="!loading && !staffings.length">
              <td colspan="10" class="empty-cell">
                {{ tr("noStaffData") }}
              </td>
            </tr>
          </tbody>
        </table>

        <div class="mobile-staff-cards">
          <article
            v-for="item in staffings"
            :key="`mobile-${item.id}`"
            class="mobile-staff-card"
          >
            <div class="mobile-card-head">
              <h3>{{ item.facility_name }}</h3>

              <span class="staff-count-badge">{{
                item.occupied_positions
              }}</span>
            </div>

            <div class="mobile-card-body">
              <div class="mobile-data-row">
                <span>{{ tr("table.approvedPositions") }}</span
                ><strong>{{ item.approved_positions }}</strong>
              </div>

              <div class="mobile-data-row">
                <span>{{ tr("table.occupiedPositions") }}</span
                ><strong>{{ item.occupied_positions }}</strong>
              </div>

              <div class="mobile-data-row">
                <span>{{ tr("table.vacancies") }}</span
                ><strong>{{ item.vacancies }}</strong>
              </div>

              <div class="mobile-data-row">
                <span>{{ tr("table.absent") }}</span
                ><strong>{{ item.absent_staff }}</strong>
              </div>

              <div class="mobile-data-row">
                <span>{{ tr("table.femalePercent") }}</span
                ><strong>{{ item.female_percent }}%</strong>
              </div>

              <div class="mobile-data-row">
                <span>{{ tr("table.emergencyPlan") }}</span>

                <span
                  :class="[
                    'status-badge',

                    getStatusColor(item.has_written_emergency_plan),
                  ]"
                  >{{ displayStatus(item.has_written_emergency_plan) }}</span
                >
              </div>

              <div class="mobile-data-row text-row">
                <span>{{ tr("table.unqualifiedDepartments") }}</span
                ><strong>{{
                  displayDepartments(item.departments_without_qualified_staff)
                }}</strong>
              </div>

              <div class="mobile-data-row">
                <span>{{ tr("table.nightShift") }}</span>

                <span
                  :class="[
                    'status-badge',

                    getShiftColor(item.night_shift_adequacy),
                  ]"
                  >{{ displayStatus(item.night_shift_adequacy) }}</span
                >
              </div>

              <div class="mobile-data-row">
                <span>{{ tr("table.recentTraining") }}</span>

                <span
                  :class="[
                    'status-badge',

                    getStatusColor(item.recent_training_12_months),
                  ]"
                  >{{ displayStatus(item.recent_training_12_months) }}</span
                >
              </div>
            </div>
          </article>

          <div v-if="!loading && !staffings.length" class="mobile-empty">
            {{ tr("noStaffData") }}
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";

import { useI18n } from "vue-i18n";

import api from "@/plugins/api";

const { t, locale } = useI18n();

const pageDirection = computed(() => (locale.value === "ar" ? "rtl" : "ltr"));

const isArabic = computed(() => locale.value === "ar");

const tr = (key, params = {}) => t(`hrhPage.${key}`, params);

const loading = ref(false);

const chartsReady = ref(false);

const staffings = ref([]);

const healthFacilities = ref([]);

const hrh01 = ref(0);

const hrh03 = ref(0);

const hrh04 = ref(0);

const hrh05 = ref(0);

const hrh01Facilities = ref([]);

const hrh03Components = ref([]);

const statusLabel = computed(() => ({
  g: tr("compliant"),

  a: tr("near"),

  r: tr("gap"),
}));

const colors = { g: "#10b981", a: "#f59e0b", r: "#ef4444" };

const normalizeArray = (payload) => {
  if (Array.isArray(payload)) return payload;

  if (Array.isArray(payload?.data)) return payload.data;

  if (Array.isArray(payload?.data?.data)) return payload.data.data;

  if (Array.isArray(payload?.results)) return payload.results;

  return [];
};

const getApiNumber = (res) => {
  const d = res?.data;

  return Number(
    d?.value ??
      d?.average ??
      d?.avg ??
      d?.score ??
      d?.rate ??
      d?.percentage ??
      d?.summary?.HRH_01 ??
      d?.summary?.HRH_01_AVG ??
      d?.summary?.HRH_01_Total ??
      d?.summary?.HRH_03 ??
      d?.summary?.HRH_04 ??
      d?.summary?.HRH_05 ??
      d?.data?.value ??
      d?.data?.average ??
      d?.data?.avg ??
      d?.data?.score ??
      d?.data?.rate ??
      d?.data?.percentage ??
      0
  );
};

const fetchStaffings = async () => {
  try {
    loading.value = true;

    chartsReady.value = false;

    const [hrh01Res, hrh03Res, hrh04Res, hrh05Res, staffingRes, facilitiesRes] =
      await Promise.all([
        api.get("/health-facilities/staffCoverageRate"), // HRH-01

        api.get("/health-facilities/healthWorkforceAdequacyIndex"), // HRH-03

        api.get("/health-facilities/femaleStaffRatio"), // HRH-04

        api.get("/health-facilities/trainingCoverageRate"), // HRH-05

        api.get("/facility-staffings"),

        api.get("/health-facilities"),
      ]);

    hrh01.value = getApiNumber(hrh01Res);

    hrh03.value = getApiNumber(hrh03Res);

    hrh04.value = getApiNumber(hrh04Res);

    hrh05.value = getApiNumber(hrh05Res);

    hrh01.value = Number(
      hrh01Res.data?.summary?.HRH_01 ??
        hrh01Res.data?.summary?.HRH_01_AVG ??
        hrh01Res.data?.summary?.HRH_01_Total ??
        0
    );

    hrh01Facilities.value = (hrh01Res.data?.by_facility || []).map((item) => ({
      label: item.facility_name_ar,

      value: Number(item.filled_posts || 0),
    }));

    hrh03.value = Number(
      hrh03Res.data?.average ??
        hrh03Res.data?.summary?.HRH_03 ??
        hrh03Res.data?.summary?.HRH_03_AVG ??
        0
    );

    hrh03Components.value = (hrh03Res.data?.components || []).map((item) => ({
      label: item.component_ar,

      value: Number(item.percent ?? item.score_avg ?? 0),

      weight: Number(item.weight || 0),

      weighted_score: Number(item.weighted_score || 0),
    }));

    const staffingList = normalizeArray(staffingRes.data);

    const facilitiesList = normalizeArray(facilitiesRes.data);

    healthFacilities.value = facilitiesList.map((facility, index) => ({
      facility_key: String(facility.survey_id || facility.id || index + 1),

      survey_id: facility.survey_id || index + 1,

      facility_name_ar:
        facility.facility_name_ar ||
        facility.facility_name ||
        facility.name ||
        `${tr("facilityFallback")} #${index + 1}`,

      facility_name_en:
        facility.facility_name_en ||
        facility.facility_name_ar ||
        facility.facility_name ||
        facility.name ||
        `${tr("facilityFallback")} #${index + 1}`,
    }));

    const staffingMap = {};

    staffingList.forEach((item) => {
      const key = String(item.survey_id);

      if (!staffingMap[key]) {
        staffingMap[key] = {
          approved_positions: 0,

          occupied_positions: 0,

          female_count: 0,

          absent_count: 0,

          trained_count: 0,

          qualified_missing: [],

          night_shift_values: [],

          written_plan_count: 0,
        };
      }

      staffingMap[key].approved_positions += Number(
        item.approved_positions || 0
      );

      staffingMap[key].occupied_positions += Number(
        item.occupied_positions || 0
      );

      staffingMap[key].female_count += Number(item.female_count || 0);

      staffingMap[key].absent_count += Number(item.absent_count || 0);

      if (Number(item.trained_last_12_months || 0) === 1) {
        staffingMap[key].trained_count += 1;
      }

      if (Number(item.qualified_staff_available || 0) === 0) {
        staffingMap[key].qualified_missing.push(
          (isArabic.value
            ? item.cadre?.cadre_name_ar
            : item.cadre?.cadre_name_en) || tr("unspecified")
        );
      }

      if (Number(item.written_emergency_plan || 0) === 1) {
        staffingMap[key].written_plan_count += 1;
      }

      staffingMap[key].night_shift_values.push(
        item.night_shift_adequacy || tr("unspecified")
      );
    });

    staffings.value = healthFacilities.value.map((facility) => {
      const item = staffingMap[String(facility.survey_id)];

      if (!item) {
        return {
          id: facility.facility_key,

          facility_name: isArabic.value
            ? facility.facility_name_ar
            : facility.facility_name_en,

          approved_positions: 0,

          occupied_positions: 0,

          vacancies: 0,

          absent_staff: 0,

          female_percent: 0,

          has_written_emergency_plan: tr("unspecified"),

          departments_without_qualified_staff: tr("unspecified"),

          night_shift_adequacy: tr("unspecified"),

          recent_training_12_months: tr("unspecified"),
        };
      }

      const femalePercent = item.occupied_positions
        ? Math.round((item.female_count / item.occupied_positions) * 100)
        : 0;

      const nightShift = item.night_shift_values.includes(
        tr("status.inadequate")
      )
        ? tr("status.inadequate")
        : item.night_shift_values.includes(tr("status.partialFeminine"))
        ? tr("status.partial")
        : item.night_shift_values.includes(tr("status.adequate"))
        ? tr("status.adequate")
        : tr("unspecified");

      return {
        id: facility.facility_key,

        facility_name: isArabic.value
          ? facility.facility_name_ar
          : facility.facility_name_en,

        approved_positions: item.approved_positions,

        occupied_positions: item.occupied_positions,

        vacancies: Math.max(
          item.approved_positions - item.occupied_positions,

          0
        ),

        absent_staff: item.absent_count,

        female_percent: femalePercent,

        has_written_emergency_plan:
          item.written_plan_count > 0 ? tr("status.yes") : tr("status.no"),

        departments_without_qualified_staff: item.qualified_missing.length
          ? item.qualified_missing.join("، ")
          : tr("status.none"),

        night_shift_adequacy: nightShift,

        recent_training_12_months:
          item.trained_count > 0 ? tr("status.yes") : tr("status.no"),
      };
    });
  } catch (error) {
    console.error("Facility Staffings API Error:", error);

    staffings.value = [];

    healthFacilities.value = [];
  } finally {
    chartsReady.value = true;

    loading.value = false;
  }
};

const axisWeight = ref(null);

const axisAverage = ref(null);

const loadAxisWeight = async () => {
  try {
    const response = await api.get("/health-facilities/axis-averages");

    const payload = response.data?.data;

    axisWeight.value = payload?.staff?.weight ?? null;

    axisAverage.value = payload?.staff?.average ?? null;
  } catch (error) {
    console.error("خطأ في تحميل بيانات المحاور:", error);

    axisWeight.value = null;

    axisAverage.value = null;
  }
};

onMounted(async () => {
  await Promise.allSettled([fetchStaffings(), loadAxisWeight()]);
});

const kpiStatus = (kpi) => {
  const v = Number(kpi.val || 0);

  const std = Number(kpi.std || 0);

  if (kpi.dir === "lt") {
    if (v <= std) return "g";

    // حرج عندما تتجاوز القيمة المعيار بنسبة 30% أو أكثر.
    if (v < std * 1.3) return "a";

    return "r";
  }

  if (v >= std) return "g";

  // حرج عندما تقل القيمة عن المعيار بنسبة 30% أو أكثر.
  if (v > std * 0.7) return "a";

  return "r";
};

const statusColor = (kpi) => colors[kpiStatus(kpi)];

const achieve = (kpi) => {
  const v = Number(kpi.val || 0);

  const std = Number(kpi.std || 1);

  const max = Number(kpi.max || 100);

  if (kpi.dir === "lt") {
    return v <= std
      ? 100
      : Math.max(
          0,

          Math.round(100 - ((v - std) / Math.max(1, max - std)) * 100)
        );
  }

  return Math.min(100, Math.round((v / std) * 100));
};

const barWidth = (kpi) =>
  Math.min(
    100,

    Math.max(3, (Number(kpi.val || 0) / Number(kpi.max || 100)) * 100)
  );

const hrhCharts = computed(() => ({
  "HRH-01": {
    type: "bar",

    label: tr("charts.staffCoverage"),

    data: hrh01Facilities.value.map((x) => x.value),

    labels: hrh01Facilities.value.map((x) => x.label),
  },

  "HRH-03": {
    type: "bar",

    horizontal: true,

    label: tr("charts.workforceComponents"),

    data: hrh03Components.value.map((x) => x.value),

    labels: hrh03Components.value.map((x) => x.label),
  },

  "HRH-04": {
    type: "radialBar",

    label: tr("charts.femaleStaffAverage"),

    value: hrh04.value,
  },

  "HRH-05": {
    type: "radialBar",

    label: tr("charts.trainingAverage"),

    value: hrh05.value,
  },
}));

const hrhKpis = computed(() => [
  {
    code: "HRH-01",

    name: tr("kpis.staffCoverage"),

    val: hrh01.value,

    unit: "%",

    std: 60,

    dir: "gt",

    max: 100,

    composite: true,

    flag: hrh01.value < 60,

    chart: hrhCharts.value["HRH-01"],
  },

  {
    code: "HRH-03",

    name: tr("kpis.workforceAdequacy"),

    val: hrh03.value,

    unit: "/100",

    std: 70,

    dir: "gt",

    max: 100,

    composite: true,

    flag: hrh03.value < 70,

    chart: hrhCharts.value["HRH-03"],
  },

  {
    code: "HRH-04",

    name: tr("kpis.femaleStaff"),

    val: hrh04.value,

    unit: "%",

    std: 30,

    dir: "gt",

    max: 100,

    composite: true,

    flag: hrh04.value < 30,

    chart: hrhCharts.value["HRH-04"],
  },

  {
    code: "HRH-05",

    name: tr("kpis.trainingCoverage"),

    val: hrh05.value,

    unit: "%",

    std: 40,

    dir: "gt",

    max: 100,

    composite: true,

    flag: hrh05.value < 40,

    chart: hrhCharts.value["HRH-05"],
  },
]);

const hrhAchievedCount = computed(
  () => hrhKpis.value.filter((k) => kpiStatus(k) === "g").length
);

const hrhBelowStandardCount = computed(
  () => hrhKpis.value.filter((k) => kpiStatus(k) === "a").length
);

const hrhCriticalCount = computed(
  () => hrhKpis.value.filter((k) => kpiStatus(k) === "r").length
);

const totalApproved = computed(() =>
  staffings.value.reduce((sum, i) => sum + Number(i.approved_positions || 0), 0)
);

const totalOccupied = computed(() =>
  staffings.value.reduce((sum, i) => sum + Number(i.occupied_positions || 0), 0)
);

const avgVacancies = computed(() => {
  if (!staffings.value.length) return 0;

  const total = staffings.value.reduce(
    (sum, i) => sum + Number(i.vacancies || 0),

    0
  );

  return (total / staffings.value.length).toFixed(1);
});

const avgAbsentStaff = computed(() => {
  if (!staffings.value.length) return 0;

  const total = staffings.value.reduce(
    (sum, i) => sum + Number(i.absent_staff || 0),

    0
  );

  return (total / staffings.value.length).toFixed(1);
});

const avgFemalePercent = computed(() => {
  if (!staffings.value.length) return 0;

  const total = staffings.value.reduce(
    (sum, i) => sum + Number(i.female_percent || 0),

    0
  );

  return Math.round(total / staffings.value.length);
});

const emergencyPlanCount = computed(
  () =>
    staffings.value.filter(
      (i) => i.has_written_emergency_plan === tr("status.yes")
    ).length
);

const cleanValue = (value) => {
  if (value === null || value === undefined || value === "")
    return tr("unspecified");

  return String(value);
};

const countMulti = (field) => {
  const result = {};

  staffings.value.forEach((item) => {
    cleanValue(item[field])
      .split("،")

      .forEach((value) => {
        const key = value.trim() || tr("unspecified");

        result[key] = (result[key] || 0) + 1;
      });
  });

  return result;
};

const countBy = (field) => {
  const result = {};

  staffings.value.forEach((item) => {
    const key = cleanValue(item[field]);

    result[key] = (result[key] || 0) + 1;
  });

  return result;
};

const departmentData = computed(() =>
  countMulti("departments_without_qualified_staff")
);

const nightShiftData = computed(() => countBy("night_shift_adequacy"));

const trainingData = computed(() => countBy("recent_training_12_months"));

const departmentLabels = computed(() => Object.keys(departmentData.value));

const departmentSeries = computed(() => Object.values(departmentData.value));

const nightShiftLabels = computed(() => Object.keys(nightShiftData.value));

const nightShiftSeries = computed(() => Object.values(nightShiftData.value));

const trainingLabels = computed(() => Object.keys(trainingData.value));

const trainingSeries = computed(() => Object.values(trainingData.value));

const facilityLabels = computed(() =>
  staffings.value.map((item) => item.facility_name)
);

const positionsSeries = computed(() => [
  {
    name: tr("table.approvedPositions"),

    data: staffings.value.map((item) => item.approved_positions),
  },

  {
    name: tr("table.occupiedPositions"),

    data: staffings.value.map((item) => item.occupied_positions),
  },
]);

const miniBaseChart = {
  chart: {
    toolbar: { show: false },

    fontFamily: isArabic.value ? "Cairo" : "Inter",

    foreColor: "#7890ad",
  },

  dataLabels: { enabled: false },

  legend: { position: "bottom" },

  grid: { borderColor: "#d7e0eb" },

  noData: { text: tr("noData") },
};

const miniBarOptions = (labels = [], horizontal = false) => ({
  ...miniBaseChart,

  plotOptions: { bar: { horizontal, borderRadius: 6, columnWidth: "45%" } },

  xaxis: {
    categories: labels.length ? labels : [tr("noData")],

    labels: { rotate: horizontal ? 0 : -35, trim: true },
  },
});

const miniDonutOptions = (labels = []) => ({
  ...miniBaseChart,

  labels: labels.length ? labels : [tr("noData")],
});

const radialOptions = (value) => ({
  ...miniBaseChart,

  chart: {
    type: "radialBar",

    toolbar: { show: false },

    fontFamily: isArabic.value ? "Cairo" : "Inter",
  },

  plotOptions: {
    radialBar: {
      startAngle: -135,

      endAngle: 135,

      hollow: { size: "62%" },

      track: { background: "#e2e8f0" },

      dataLabels: {
        name: { show: false },

        value: {
          fontSize: "30px",

          fontWeight: 900,

          color: value >= 80 ? "#10b981" : value >= 50 ? "#f59e0b" : "#ef4444",

          formatter: (val) => `${Number(val).toFixed(1)}%`,
        },
      },
    },
  },

  colors: [value >= 80 ? "#10b981" : value >= 50 ? "#f59e0b" : "#ef4444"],
});

const kpiApex = (kpi) => {
  const ch = kpi.chart;

  if (kpi.code === "HRH-01") {
    const labels = hrh01Facilities.value.map((x) => x.label);

    const data = hrh01Facilities.value.map((x) => Number(x.value || 0));

    return {
      type: "bar",

      series: [
        {
          name: "HRH-01",

          data,
        },
      ],

      options: {
        chart: {
          type: "bar",

          toolbar: { show: false },

          fontFamily: isArabic.value ? "Cairo" : "Inter",
        },

        plotOptions: {
          bar: {
            horizontal: false,

            borderRadius: 6,

            columnWidth: "45%",
          },
        },

        xaxis: {
          categories: labels,

          labels: {
            rotate: -45,

            trim: true,
          },
        },

        yaxis: {
          min: 0,

          max: 100,

          tickAmount: 4,
        },

        dataLabels: { enabled: false },

        legend: { show: false },
      },
    };
  }

  if (!ch) {
    return {
      type: "bar",

      series: [],

      options: miniBarOptions([]),
    };
  }

  if (ch.type === "radialBar") {
    const value = Number(ch.value || 0);

    return {
      type: "radialBar",

      series: [value],

      options: radialOptions(value),
    };
  }

  if (ch.type === "donut") {
    return {
      type: "donut",

      series: ch.data?.length ? ch.data : [0],

      options: miniDonutOptions(ch.labels || []),
    };
  }

  return {
    type: "bar",

    series: [{ name: kpi.code, data: ch.data?.length ? ch.data : [0] }],

    options: miniBarOptions(ch.labels || [], !!ch.horizontal),
  };
};

const donutOptions = (labels = []) => ({
  chart: {
    type: "donut",

    toolbar: { show: false },

    fontFamily: isArabic.value ? "Cairo" : "Inter",
  },

  labels: labels.length ? labels : [tr("unspecified")],

  legend: { position: "bottom" },

  noData: { text: tr("noData") },
});

const shortLabel = (text, max = 14) => {
  if (!text) return tr("unspecified");

  return String(text).length > max
    ? `${String(text).substring(0, max)}...`
    : text;
};

const barOptions = (labels = []) => ({
  chart: {
    type: "bar",

    toolbar: { show: false },

    fontFamily: isArabic.value ? "Cairo" : "Inter",
  },

  xaxis: {
    categories: labels.length ? labels : [tr("unspecified")],

    labels: {
      rotate: -35,

      trim: true,

      formatter: (value) => shortLabel(value, 14),

      style: { fontSize: "11px" },
    },
  },

  tooltip: {
    x: {
      formatter: (value, opts) => labels[opts.dataPointIndex] || value,
    },
  },

  plotOptions: {
    bar: {
      borderRadius: 6,

      columnWidth: "45%",
    },
  },

  dataLabels: { enabled: false },

  noData: { text: tr("noData") },
});

const normalizeStatusKey = (value) => {
  const text = String(value ?? "")
    .trim()

    .toLowerCase();

  const map = {
    نعم: "yes",

    yes: "yes",

    لا: "no",

    no: "no",

    جزئي: "partial",

    partial: "partial",

    جزئية: "partial",

    كافية: "adequate",

    adequate: "adequate",

    "غير كافية": "inadequate",

    inadequate: "inadequate",

    "غير محدد": "unspecified",

    unspecified: "unspecified",

    "لا يوجد": "none",

    none: "none",
  };

  return map[text] || "unspecified";
};

const displayStatus = (value) => tr(`status.${normalizeStatusKey(value)}`);

const displayDepartments = (value) => {
  const text = String(value ?? "").trim();

  if (!text || ["غير محدد", "unspecified"].includes(text.toLowerCase()))
    return tr("unspecified");

  if (["لا يوجد", "none"].includes(text.toLowerCase()))
    return tr("status.none");

  return text;
};

const getStatusColor = (value) => {
  const key = normalizeStatusKey(value);

  if (key === "yes") return "status-green";

  if (key === "partial") return "status-orange";

  return "status-red";
};

const getShiftColor = (value) => {
  const key = normalizeStatusKey(value);

  if (key === "adequate") return "status-green";

  if (key === "partial") return "status-orange";

  return "status-red";
};
</script>

<style scoped>
.hrh-page {
  min-height: 100vh;

  background: #f4f6f9;

  padding: 24px;

  color: #071126;

  font-family: "Inter", "Cairo", sans-serif;
}

.page-loading {
  position: fixed;

  inset: 0;

  background: rgba(244, 246, 249, 0.92);

  backdrop-filter: blur(6px);

  z-index: 9999;

  display: flex;

  align-items: center;

  justify-content: center;
}

.loader-card {
  width: 320px;

  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 22px;

  padding: 34px 24px;

  text-align: center;

  box-shadow: 0 20px 60px rgba(15, 35, 64, 0.15);
}

.loader-ring {
  width: 64px;

  height: 64px;

  border: 6px solid #e2e8f0;

  border-top-color: #7c3aed;

  border-radius: 50%;

  margin: 0 auto 18px;

  animation: spin 0.8s linear infinite;
}

.loader-card h3 {
  font-size: 18px;

  font-weight: 900;

  color: #7c3aed;

  margin-bottom: 8px;
}

.loader-card p {
  color: #64748b;

  font-size: 13px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.page-header {
  text-align: right;

  margin-bottom: 26px;
}

.page-header h1 {
  font-size: 28px;

  font-weight: 900;

  margin-bottom: 8px;

  color: #061226;
}

.page-header p {
  color: #52657d;

  font-size: 14px;
}

.top-row {
  display: flex;

  align-items: center;

  justify-content: space-between;

  margin-bottom: 18px;
}

.counter {
  color: #64748b;

  font-size: 14px;
}

.top-row select {
  width: 225px;

  height: 50px;

  background: #fff;

  border: 1px solid #cbd5e1;

  border-radius: 8px;

  padding: 0 14px;

  font-family: inherit;
}

.axis-summary,
.measured-box {
  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 18px;

  padding: 18px;

  margin-bottom: 18px;

  box-shadow: 0 8px 24px rgba(15, 35, 64, 0.05);
}

.axis-card-header {
  display: flex;

  justify-content: space-between;

  gap: 20px;

  align-items: center;
}

.axis-card-header h2 {
  font-size: 22px;

  font-weight: 900;

  color: #7c3aed;

  margin-bottom: 6px;
}

.axis-card-header p {
  color: #64748b;

  font-size: 13px;

  margin-bottom: 8px;
}

.axis-card-header strong {
  color: #071126;

  font-size: 14px;
}

.axis-score {
  min-width: 130px;

  text-align: center;

  background: #f3e8ff;

  color: #7c3aed;

  border-radius: 18px;

  padding: 18px;
}

.axis-score span {
  display: block;

  font-size: 34px;

  font-weight: 900;
}

.axis-score small {
  font-weight: 800;
}

.axis-stats {
  display: grid;

  grid-template-columns: repeat(3, 1fr);

  gap: 12px;

  margin-top: 16px;
}

.axis-stats div {
  background: #f8fafc;

  border: 1px solid #e2e8f0;

  border-radius: 14px;

  padding: 14px;

  text-align: center;
}

.axis-stats b {
  display: block;

  font-size: 26px;

  color: #7c3aed;
}

.axis-stats span {
  font-size: 13px;

  font-weight: 800;

  color: #64748b;
}

.axis-card {
  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 12px;

  padding: 22px;

  box-shadow: 0 1px 4px rgba(15, 35, 64, 0.05);
}

.axis-title {
  border-bottom: 1px solid #dbe3ee;

  padding-bottom: 14px;

  margin-bottom: 16px;
}

.axis-title > div {
  display: flex;

  align-items: center;

  gap: 8px;

  flex-wrap: wrap;
}

.dot {
  width: 12px;

  height: 12px;

  background: #7c3aed;

  border-radius: 4px;
}

.axis-title strong {
  font-size: 18px;

  font-weight: 900;
}

.axis-title small {
  font-size: 12px;

  color: #64748b;
}

.desc {
  color: #64748b;

  font-size: 14px;

  margin-bottom: 14px;
}

.section-title {
  font-size: 16px;

  font-weight: 900;

  color: #7c3aed;

  margin-bottom: 12px;
}

.section-sub {
  color: #64748b;

  font-size: 13px;

  margin-bottom: 14px;
}

.measured-grid {
  display: grid;

  grid-template-columns: 1fr;

  gap: 14px;
}

.measure-card {
  background: #f8fafc;

  border: 1px solid #e2e8f0;

  border-radius: 14px;

  padding: 15px;
}

.measure-card span {
  display: block;

  font-size: 13px;

  font-weight: 800;

  color: #334155;
}

.measure-card b {
  display: block;

  font-size: 30px;

  font-weight: 900;

  color: #7c3aed;

  margin: 8px 0;
}

.measure-card small {
  color: #64748b;

  font-weight: 700;
}

.info-box {
  background: #f8fafc;

  border: 1px solid #dbe3ee;

  border-radius: 12px;

  padding: 16px 18px;

  margin-bottom: 12px;

  line-height: 2;

  font-size: 13px;

  color: #334155;
}

.info-box strong {
  color: #7c3aed;
}

.kpi-grid {
  display: grid;

  grid-template-columns: repeat(4, 1fr);

  gap: 14px;

  margin: 18px 0;
}

.kpi-card {
  border-radius: 16px;

  padding: 20px;

  text-align: center;

  border: 1px solid #dbe3ee;
}

.kpi-value {
  font-size: 28px;

  font-weight: 900;
}

.kpi-label {
  margin-top: 8px;

  font-size: 13px;

  font-weight: 800;
}

.kpi-card.red {
  background: #fdecea;

  color: #c62828;
}

.kpi-card.orange {
  background: #fff4e5;

  color: #d4821a;
}

.kpi-card.green {
  background: #e8f5e9;

  color: #1b5e20;
}

.kpi-card.purple {
  background: #f3e8ff;

  color: #7c3aed;
}

.charts-grid {
  display: grid;

  grid-template-columns: repeat(2, 1fr);

  gap: 14px;

  margin: 18px 0;
}

.chart-card {
  background: #f8fafc;

  border: 1px solid #dbe3ee;

  border-radius: 12px;

  padding: 16px;

  min-height: 370px;
}

.table-wrap {
  overflow-x: auto;

  border: 1px solid #dbe3ee;

  border-radius: 10px;

  background: #fff;
}

.mb-table,
.indicators-table {
  margin-bottom: 18px;
}

.table-title {
  background: #7c3aed;

  color: #fff;

  padding: 14px 18px;

  font-weight: 800;

  display: flex;

  justify-content: space-between;

  align-items: center;
}

.table-title span {
  background: rgba(255, 255, 255, 0.15);

  padding: 4px 10px;

  border-radius: 999px;

  font-size: 12px;
}

table {
  width: 100%;

  min-width: 1100px;

  border-collapse: collapse;

  background: #fff;
}

.staff-table {
  min-width: 1200px;
}

.indicators-table table {
  min-width: 1300px;
}

th {
  background: #f8fafc;

  color: #7c3aed;

  font-size: 13px;

  font-weight: 900;

  padding: 15px 14px;

  text-align: right;

  white-space: nowrap;

  border-bottom: 1px solid #e2e8f0;
}

td {
  padding: 14px;

  border-bottom: 1px solid #dbe3ee;

  font-size: 13px;

  line-height: 1.8;

  vertical-align: top;
}

tbody tr:hover {
  background: #f8fafc;
}

.code {
  font-weight: 900;

  direction: ltr;

  text-align: center;

  white-space: nowrap;

  color: #7c3aed;
}

.name {
  font-weight: 900;

  min-width: 220px;
}

.unit {
  text-align: center;

  white-space: nowrap;
}

.status-badge {
  display: inline-flex;

  align-items: center;

  gap: 7px;

  border-radius: 999px;

  padding: 7px 13px;

  font-size: 12px;

  font-weight: 700;
}

.status-green {
  background: #dcfce7;

  color: #166534;
}

.status-orange {
  background: #ffedd5;

  color: #9a3412;
}

.status-red {
  background: #fee2e2;

  color: #991b1b;
}

.empty-cell {
  text-align: center;

  color: #64748b;

  padding: 20px !important;
}

@media (max-width: 1000px) {
  .charts-grid {
    grid-template-columns: 1fr;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .axis-card-header {
    flex-direction: column;

    align-items: stretch;
  }
}

@media (max-width: 900px) {
  .hrh-page {
    padding: 16px 12px;
  }

  .page-header h1 {
    font-size: 21px;
  }

  .top-row {
    flex-direction: column-reverse;

    align-items: stretch;

    gap: 12px;
  }

  .top-row select {
    width: 100%;
  }

  .axis-card {
    padding: 14px;
  }

  .axis-stats {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

/* ===== Indicator mini cards copied from axis indicators page ===== */

.axis-indicator-grid {
  display: grid;

  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));

  gap: 14px;

  margin-bottom: 18px;
}

.axis-indicator-card {
  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 16px;

  padding: 16px;

  position: relative;

  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
}

.axis-indicator-card.flag {
  border-color: rgba(239, 68, 68, 0.38);
}

.axis-indicator-card.flag::after {
  content: "⚑ مؤشر حرج";

  position: absolute;

  top: 13px;

  left: 14px;

  font-size: 10px;

  color: #ef4444;

  font-weight: 900;
}

.axis-indicator-top {
  display: flex;

  gap: 8px;

  margin-bottom: 10px;
}

.axis-indicator-card h3 {
  font-size: 15px;

  font-weight: 900;

  color: #071126;

  margin: 0 0 12px;

  min-height: 42px;
}

.axis-indicator-value {
  display: flex;

  align-items: baseline;

  gap: 7px;

  margin-bottom: 10px;
}

.axis-indicator-value strong {
  font-size: 32px;

  font-weight: 900;

  direction: ltr;
}

.axis-indicator-value span {
  color: #64748b;
}

.axis-indicator-value em {
  margin-right: auto;

  font-style: normal;

  background: #f8fafc;

  border: 1px solid #e2e8f0;

  border-radius: 8px;

  padding: 5px 9px;

  color: #475569;

  font-size: 11px;

  font-weight: 800;
}

.axis-indicator-foot {
  display: flex;

  justify-content: space-between;

  align-items: center;

  margin: 8px 0 13px;

  font-size: 12px;

  font-weight: 700;

  color: #64748b;
}

.axis-mini-chart {
  background: #f8fafc;

  border: 1px solid #dbe3ee;

  border-radius: 14px;

  padding: 14px;
}

.axis-mini-chart h4 {
  font-size: 14px;

  font-weight: 900;

  color: inherit;

  margin: 0 0 10px;
}

.composite {
  background: #f5f3ff;

  border: 1px solid #ddd6fe;

  color: #7c3aed;

  border-radius: 7px;

  padding: 3px 8px;

  font-size: 10px;

  font-weight: 900;
}

.gauge {
  height: 9px;

  background: #e2e8f0;

  border-radius: 999px;

  overflow: hidden;
}

.gauge div {
  height: 100%;

  border-radius: 999px;
}

.rag {
  border-radius: 7px;

  padding: 4px 10px;

  font-weight: 900;
}

.rag.g {
  background: rgba(16, 185, 129, 0.12);

  color: #10b981;
}

.rag.a {
  background: rgba(245, 158, 11, 0.14);

  color: #f59e0b;
}

.rag.r {
  background: rgba(239, 68, 68, 0.12);

  color: #ef4444;
}

/* ================= RTL / LTR ================= */

.hrh-page[dir="rtl"] {
  direction: rtl;

  font-family: "Cairo", sans-serif;
}

.hrh-page[dir="ltr"] {
  direction: ltr;

  font-family: "Inter", "Cairo", sans-serif;
}

.hrh-page[dir="rtl"] .page-header,
.hrh-page[dir="rtl"] th,
.hrh-page[dir="rtl"] td,
.hrh-page[dir="rtl"] .section-title {
  text-align: right;
}

.hrh-page[dir="ltr"] .page-header,
.hrh-page[dir="ltr"] th,
.hrh-page[dir="ltr"] td,
.hrh-page[dir="ltr"] .section-title {
  text-align: left;
}

.hrh-page[dir="ltr"] .axis-indicator-value em {
  margin-left: auto;

  margin-right: 0;
}

.hrh-page[dir="rtl"] .axis-indicator-value em {
  margin-right: auto;

  margin-left: 0;
}

.hrh-page[dir="rtl"] .axis-indicator-card.flag::after {
  left: 14px;

  right: auto;
}

.hrh-page[dir="ltr"] .axis-indicator-card.flag::after {
  right: 14px;

  left: auto;
}

.mobile-staff-cards {
  display: none;
}

@media (max-width: 768px) {
  .hrh-page {
    padding: 12px 8px 28px;

    overflow-x: hidden;
  }

  .page-header {
    margin-bottom: 16px;
  }

  .page-header h1 {
    font-size: 20px;

    line-height: 1.5;
  }

  .page-header p {
    font-size: 12px;

    line-height: 1.8;
  }

  .top-row {
    flex-direction: column-reverse;

    align-items: stretch;

    gap: 10px;
  }

  .top-row select {
    width: 100%;
  }

  .axis-summary {
    padding: 13px;

    border-radius: 15px;
  }

  .axis-card-header {
    flex-direction: column;

    align-items: stretch;

    gap: 12px;
  }

  .axis-card-header h2 {
    font-size: 18px;
  }

  .axis-score {
    width: 100%;

    min-width: 0;

    padding: 13px;

    border-radius: 14px;
  }

  .axis-score span {
    font-size: 28px;
  }

  .axis-stats {
    grid-template-columns: repeat(3, minmax(0, 1fr));

    gap: 7px;
  }

  .axis-stats div {
    padding: 10px 5px;

    border-radius: 11px;
  }

  .axis-stats b {
    font-size: 21px;
  }

  .axis-stats span {
    font-size: 10px;

    line-height: 1.5;
  }

  .axis-indicator-grid {
    grid-template-columns: minmax(0, 1fr);

    gap: 12px;
  }

  .axis-indicator-card {
    padding: 13px;
  }

  .axis-indicator-card h3 {
    min-height: 0;

    font-size: 14px;
  }

  .axis-indicator-value {
    flex-wrap: wrap;
  }

  .axis-indicator-value strong {
    font-size: 28px;
  }

  .axis-indicator-value em {
    width: 100%;

    margin-inline: 0;

    text-align: center;
  }

  .axis-mini-chart {
    padding: 8px;

    overflow-x: auto;

    -webkit-overflow-scrolling: touch;
  }

  .axis-mini-chart :deep(.apexcharts-canvas) {
    max-width: 100% !important;
  }

  .axis-card {
    padding: 12px 8px;
  }

  .charts-grid,
  .kpi-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .staff-table {
    display: none;
  }

  .table-wrap {
    overflow: visible;

    border: 0;

    background: transparent;
  }

  .table-title {
    padding: 12px 13px;

    border-radius: 12px;

    font-size: 14px;
  }

  .mobile-staff-cards {
    display: grid;

    gap: 12px;

    margin-top: 12px;
  }

  .mobile-staff-card {
    padding: 14px;

    background: #fff;

    border: 1px solid #dbe3ee;

    border-inline-start: 5px solid #7c3aed;

    border-radius: 15px;

    box-shadow: 0 6px 18px rgba(15, 35, 64, 0.06);
  }

  .mobile-card-head {
    display: flex;

    align-items: flex-start;

    justify-content: space-between;

    gap: 10px;

    padding-bottom: 12px;

    margin-bottom: 4px;

    border-bottom: 1px solid #e2e8f0;
  }

  .mobile-card-head h3 {
    min-width: 0;

    margin: 0;

    color: #7c3aed;

    font-size: 15px;

    font-weight: 900;

    line-height: 1.7;

    overflow-wrap: break-word;
  }

  .staff-count-badge {
    flex-shrink: 0;

    min-width: 38px;

    padding: 6px 10px;

    color: #6d28d9;

    background: #f3e8ff;

    border-radius: 999px;

    text-align: center;

    font-weight: 900;
  }

  .mobile-card-body {
    display: grid;
  }

  .mobile-data-row {
    display: flex;

    align-items: center;

    justify-content: space-between;

    gap: 14px;

    padding: 10px 0;

    border-bottom: 1px dashed #e2e8f0;
  }

  .mobile-data-row:last-child {
    border-bottom: 0;
  }

  .mobile-data-row span:first-child {
    color: #64748b;

    font-size: 12px;

    font-weight: 800;
  }

  .mobile-data-row strong {
    min-width: 0;

    color: #071126;

    font-size: 14px;

    font-weight: 900;

    text-align: end;

    overflow-wrap: break-word;
  }

  .mobile-data-row\.text-row {
    align-items: flex-start;
  }

  .mobile-data-row\.text-row strong {
    max-width: 55%;

    line-height: 1.7;
  }

  .mobile-empty {
    padding: 24px;

    text-align: center;

    color: #64748b;

    background: #fff;

    border: 1px dashed #cbd5e1;

    border-radius: 14px;

    font-weight: 800;
  }

  .loader-card {
    width: calc(100% - 28px);

    padding: 28px 18px;
  }
}

@media (max-width: 380px) {
  .axis-stats {
    grid-template-columns: 1fr;
  }

  .mobile-card-head {
    flex-direction: column;
  }
}
</style>
